import React, { useCallback, useMemo, useContext } from 'react';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import { SgDateUtil, SgSessionStorageService } from 'saga-modules';
import SgIpidUtil from 'sg-ipid-util';
import SgIpidDetails from 'sg-ipid-details';
import SmiConstants from 'sg-smi-constants';
import CommonConstants from 'sg-common-constants';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import moment from 'moment';
import IpidConfig from './SgSmiIpidConfig.json';
import messages from './SgSmiIpid.messages';
import metadata from './SgSmiIpid.metadata.json5';

function SgSmiIpid(props) {
    const {
        model
    } = props;

    const translator = useContext(TranslatorContext);
    const { smiInsurance } = model.lobData.privateMedical.coverables;
    const coverLevel = smiInsurance.selectedCoverLevel;
    const hasAdditionalPeople = model.lobData.privateMedical.coverables.smiInsuredPersons.length > 1;
    const smiEffectiveDate = moment(model.baseData.periodStartDate).isSameOrAfter(appConfig.smiEffectiveDate.startDate, 'day');

    const allIpidConditions = useMemo(() => {
        const ipidConditions = {};
        ipidConditions.hasPlanTypeSaverPlus = coverLevel === SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS;
        ipidConditions.hasPlanTypeSecure = coverLevel === SmiConstants.COVER_LEVEL_CODES.SECURE;
        ipidConditions.hasPlanTypeSuper = coverLevel === SmiConstants.COVER_LEVEL_CODES.SUPER;

        ipidConditions.hasNotPlanTypeSaverPlus = coverLevel !== SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS;
        ipidConditions.hasNotPlanTypeSuper = coverLevel !== SmiConstants.COVER_LEVEL_CODES.SUPER;

        ipidConditions.hasAddedExtendedCancerCover = smiInsurance.hadAddedExtendedCancerCover;
        ipidConditions.hasHypertensionCoverAddon = smiInsurance.hadAddedHighBloodPressure;
        ipidConditions.hasCashBenefitsAddon = smiInsurance.hasAddedHealthCashBenefits;
        ipidConditions.hasNotCashBenefitsAddon = !smiInsurance.hasAddedHealthCashBenefits;
        ipidConditions.hasFourWeekWaitAddon = smiInsurance.hasAddedFourWeekWait;

        ipidConditions.hasAddedNCDProtection = smiInsurance.hasAddedNCDProtection;
        ipidConditions.hasSagaCountryHospitalAddon = smiInsurance.hospitalList === SmiConstants.HOSPITAL_OPTIONS_CODE.COUNTRYWIDE_HOSPITAL_LIST;
        ipidConditions.hasCountryWideHospitalAddon = smiInsurance.hospitalList === SmiConstants.HOSPITAL_OPTIONS_CODE.LONDON_UPGRADE;
        ipidConditions.isGuidedCare = smiInsurance.hospitalList === SmiConstants.HOSPITAL_OPTIONS_CODE.GUIDED_CARE;
        ipidConditions.isNotGuidedCare = smiInsurance.hospitalList !== SmiConstants.HOSPITAL_OPTIONS_CODE.GUIDED_CARE;
        ipidConditions.hasTailoredCover = ipidConditions.hasCashBenefitsAddon || smiInsurance.hadAddedExtendedCancerCover;
        ipidConditions.hasTailoredCoverAndAdditionalPeople = (ipidConditions.hasCashBenefitsAddon
            || ipidConditions.hasHypertensionCoverAddon || ipidConditions.hasAddedExtendedCancerCover) && hasAdditionalPeople;
        ipidConditions.hasAddedFourWeekWaitAfterEffectiveDate = smiEffectiveDate && smiInsurance.hasAddedFourWeekWait;
        ipidConditions.hasAddedFourWeekWaitBeforeEffectiveDate = !smiEffectiveDate && smiInsurance.hasAddedFourWeekWait;
        return ipidConditions;
    }, [model]);

    const coverLevelMessages = useMemo(() => {
        return {
            commonCoveredCountries: ['WhereAmICovered'],
            commonObligations: ['MyObligations1']
        };
    }, []);

    const getAllParamValues = useMemo(() => {
        const paramValues = {};
        switch (coverLevel) {
            case SmiConstants.COVER_LEVEL_CODES.SUPER:
                paramValues.NHSCashBenefitAmountPerNight = SmiConstants.NHS_CASH_BENEFIT_LEVEL_PER_NIGHT.SUPER;
                paramValues.NHSCashBenefitAmountPerYear = SmiConstants.NHS_CASH_BENEFIT_LEVEL_PER_YEAR.SUPER;
                paramValues.amountOPConsultationsLevel = SmiConstants.AMOUNT_OP_CONSULTATIONS_LEVEL.SUPER;
                paramValues.amountOPYearConsultationsLevel = SmiConstants.AMOUNT_OP_YEAR_CONSULTATIONS_LEVEL.SUPER;
                break;
            case SmiConstants.COVER_LEVEL_CODES.SECURE:
                paramValues.NHSCashBenefitAmountPerNight = SmiConstants.NHS_CASH_BENEFIT_LEVEL_PER_NIGHT.SECURE;
                paramValues.NHSCashBenefitAmountPerYear = SmiConstants.NHS_CASH_BENEFIT_LEVEL_PER_YEAR.SECURE;
                paramValues.amountOPConsultationsLevel = SmiConstants.AMOUNT_OP_CONSULTATIONS_LEVEL.SECURE;
                paramValues.amountOPYearConsultationsLevel = SmiConstants.AMOUNT_OP_YEAR_CONSULTATIONS_LEVEL.SECURE;
                break;
            case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
                paramValues.NHSCashBenefitAmountPerNight = SmiConstants.NHS_CASH_BENEFIT_LEVEL_PER_NIGHT.SAVER_PLUS;
                paramValues.NHSCashBenefitAmountPerYear = SmiConstants.NHS_CASH_BENEFIT_LEVEL_PER_YEAR.SAVER_PLUS;
                paramValues.amountOPConsultationsLevel = SmiConstants.AMOUNT_OP_CONSULTATIONS_LEVEL.SAVER_PLUS;
                paramValues.amountOPYearConsultationsLevel = SmiConstants.AMOUNT_OP_YEAR_CONSULTATIONS_LEVEL.SAVER_PLUS;
                break;
            default:
        }
        return paramValues;
    }, [coverLevel]);

    const messageParams = useMemo(() => {
        const {
            NHSCashBenefitAmountPerNight,
            NHSCashBenefitAmountPerYear,
            amountOPConsultationsLevel,
            amountOPYearConsultationsLevel,
            numberOfWeeks
        } = getAllParamValues;

        return {
            amountPerNight: NHSCashBenefitAmountPerNight,
            maxAmountPerYear: NHSCashBenefitAmountPerYear,
            amountOPConsultations: amountOPConsultationsLevel,
            amountOPYearConsultations: amountOPYearConsultationsLevel,
            numberOfWeeks
        };
    }, [getAllParamValues]);

    const conditionsApply = (covers, item) => {
        const conditions = [];
        if (item.productType) {
            conditions.push(covers[item.productType]);
        }
        if (item.coverLevel) {
            conditions.push(covers[item.coverLevel]);
        }
        return _.every(conditions, Boolean);
    };

    const getMessages = useCallback((cover, product, type) => {
        const productConfig = IpidConfig[product];
        return SgIpidUtil.getMessages(cover, product, type, productConfig, conditionsApply, messages, messageParams, translator);
    }, [messageParams, translator]);

    const initCommonData = useCallback((key, coverLevelCode, hasFourWeekWait) => {
        let productType;
        if (hasFourWeekWait) {
            switch (coverLevelCode) {
                case SmiConstants.COVER_LEVEL_CODES.SUPER:
                    productType = SmiConstants.FOUR_WEEK_WAIT_ADDON_LEVEL.SUPER_4;
                    break;
                case SmiConstants.COVER_LEVEL_CODES.SECURE:
                    productType = SmiConstants.FOUR_WEEK_WAIT_ADDON_LEVEL.SECURE_4;
                    break;
                case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
                    productType = SmiConstants.FOUR_WEEK_WAIT_ADDON_LEVEL.SAVER_PLUS_4;
                    break;
                default:
            }
        } else {
            switch (coverLevelCode) {
                case SmiConstants.COVER_LEVEL_CODES.SUPER:
                    productType = SmiConstants.PRODUCT_TYPE.SUPER;
                    break;
                case SmiConstants.COVER_LEVEL_CODES.SECURE:
                    productType = SmiConstants.PRODUCT_TYPE.SECURE;
                    break;
                case SmiConstants.COVER_LEVEL_CODES.SAVER_PLUS:
                    productType = SmiConstants.PRODUCT_TYPE.SAVER_PLUS;
                    break;
                default:
            }
        }
        const data = {
            insuredList: getMessages(allIpidConditions, key, `${CommonConstants.IPID_PAGE.Insured}`),
            notInsuredList: getMessages(allIpidConditions, key, `${CommonConstants.IPID_PAGE.NotInsured}`),
            restrictionsList: getMessages(allIpidConditions, key, `${CommonConstants.IPID_PAGE.Restrictions}`),
            coveredList: [],
            obligationsList: [],
            header: {
                title: translator(messages.TitleSmi),
                suffix: coverLevelCode,
                product: translator(messages[`${SmiConstants.PRODUCT_TYPE.PRODUCT}${productType}`]),
                insuranceTypeDesc: translator(messages.WhatIsTheTypeOfInsurance),
                additionalParagraph: translator(messages.HeaderAdditionalParagraph)
            },
            date: {
                periodStartDate: SgDateUtil.toLongDate(model.baseData.periodStartDate),
                periodEndDate: SgDateUtil.addDaysToLongDate(-1, model.baseData.periodEndDate)
            },
            howToCancel: translator(messages.HowToCancelText, { phone: CommonConstants.SAGA_PHONE_NUMBERS.SAGA_SMI_CUSTOMER_SERVICES_NUMBER })
        };

        // additional condition for WhereIncludedTitle visibility
        if (!allIpidConditions.hasAddedNCDProtection && !allIpidConditions.hasCashBenefitsAddon
        && !allIpidConditions.hasAddedExtendedCancerCover && !allIpidConditions.hasHypertensionCoverAddon) {
            _.remove(data.insuredList, ((el) => el.id === 'WhereIncludedTitle'));
        }
        return data;
    }, []);

    const initIpidData = useMemo(() => {
        const data = initCommonData(SmiConstants.SMI, coverLevel, smiInsurance.hasAddedFourWeekWait);
        data.obligationsList = SgIpidUtil.createListFromMessages(coverLevelMessages.commonObligations, null, messages, translator);
        if (smiInsurance.hospitalList === SmiConstants.HOSPITAL_OPTIONS_CODE.GUIDED_CARE) {
            data.obligationsList.push(translator(messages.MyObligations2));
        }
        data.coveredList = SgIpidUtil.createListFromMessages(coverLevelMessages.commonCoveredCountries, null, messages, translator);
        const isQuarterlySelected = SgSessionStorageService.getItem(SgSessionStorageService.KEY.SELECTED_PAYMENT_ID)
            === CommonConstants.PAYMENT_PLANS_ID.QUARTERLY_DD;
        data.howDoIPay = isQuarterlySelected ? translator(messages.WhenAndHowDoIPayWithQuarterly) : translator(messages.WhenAndHowDoIPay);
        data.hideCloseButton = false;
        return data;
    }, [translator, initCommonData, coverLevelMessages.commonCoveredCountries, coverLevelMessages.commonObligation, coverLevel,
        SgIpidUtil.createListFromMessages]);

    const overrides = {
        smiIpid: {
            data: initIpidData
        }
    };

    const resolvers = {
        resolveComponentMap: {
            SgIpidDetails
        },
    };

    return (
        <div>
            {renderContentFromMetadata(metadata.componentContent, overrides, resolvers)}
        </div>
    );
}

export default SgSmiIpid;
